import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DateAdapter } from '@angular/material/core';
import { EnfafModalComponent } from '../../../../../components/enfaf-modal/enfaf-modal.component';
import { EnfafDatetimePickerDarkComponent } from '../../../../../components/enfaf-datetime-picker-dark/enfaf-datetime-picker-dark.component';
import { ApiService } from '../../../../../core/services/api/api.service';
import { ToastService } from '../../../../../core/services/toast/toast.service';

export interface ModalDiplomaDatesData {
  courseId: string;
  userId: string;
  courseName: string;
  existingStartDate?: string | null;
  existingEndDate?: string | null;
}

@Component({
  selector: 'app-modal-diploma-dates',
  standalone: true,
  imports: [ReactiveFormsModule, EnfafModalComponent, EnfafDatetimePickerDarkComponent],
  templateUrl: './modal-diploma-dates.component.html',
  styleUrl: './modal-diploma-dates.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalDiplomaDatesComponent {
  protected readonly startDateControl: FormControl<Date | null>;
  protected readonly endDateControl: FormControl<Date | null>;
  protected isSaving = false;

  protected get canSave(): boolean {
    if (!this.startDateControl.value || !this.endDateControl.value) return false;
    const startDate = new Date(this.startDateControl.value);
    const endDate = new Date(this.endDateControl.value);
    return endDate >= startDate;
  }

  protected get endDateMinDate(): Date | null {
    // End date can be any date, but the picker should not suggest dates before the start date
    return this.startDateControl.value ?? null;
  }

  private readonly api = inject(ApiService);
  private readonly toast = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalDiplomaDatesData,
    private dialogRef: MatDialogRef<ModalDiplomaDatesComponent>
  ) {
    // Preload existing dates using their UTC calendar components (year/month/day)
    // instead of `DateAdapter.deserialize()`. The backend stores diploma dates as
    // UTC-midnight, date-only values; deserializing the instant directly converts it
    // into the browser's local timezone, which can shift the displayed day forward or
    // backward depending on the local offset. Rebuilding the date from its UTC Y/M/D
    // via `dateAdapter.createDate` avoids any such shift.
    this.startDateControl = new FormControl<Date | null>(this.deserializeDateOnly(data.existingStartDate) as Date | null, Validators.required);
    this.endDateControl = new FormControl<Date | null>(this.deserializeDateOnly(data.existingEndDate) as Date | null, Validators.required);
  }

  protected async onConfirm(): Promise<void> {
    if (!this.canSave) return;

    this.isSaving = true;
    this.cdr.markForCheck();

    try {
      await this.api.setStudentCourseDiplomaDates({
        courseId: this.data.courseId,
        userId: this.data.userId,
        startDate: this.toDateOnlyString(this.startDateControl.value!),
        endDate: this.toDateOnlyString(this.endDateControl.value!)
      });
      this.toast.presentSuccessToast('Fechas del diploma configuradas correctamente');
      this.dialogRef.close({ saved: true });
    } catch (error: any) {
      this.toast.presentErrorToast(error?.error?.message || 'Hubo un error al guardar las fechas');
    } finally {
      this.isSaving = false;
      this.cdr.markForCheck();
    }
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Extracts the calendar date (year/month/day) selected by the user, ignoring any
   * time component and without converting through UTC. Using `toISOString()` shifts
   * the date backwards/forwards depending on the local timezone offset, which made
   * the diploma display the day before the one actually selected.
   */
  private toDateOnlyString(value: Date): string {
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  /**
   * Rebuilds a date coming from the backend (a UTC-midnight, date-only ISO string)
   * from its UTC year/month/day, using the app-wide DateAdapter to create it. Reading
   * the UTC components directly (instead of letting the adapter convert the instant to
   * the browser's local timezone) guarantees the same calendar day is preserved
   * regardless of the local timezone offset.
   */
  private deserializeDateOnly(value?: string | null): unknown {
    if (!value) return null;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return null;
    return this.dateAdapter.createDate(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }
}
